import { getFirebaseToken } from './user-service';

declare const FIREBASE_API_KEY: string;
declare const FIREBASE_AUTH_DOMAIN: string;
declare const FIREBASE_DATABASE_URL: string;
declare const FIREBASE_PROJECT_ID: string;
declare const FIREBASE_STORAGE_BUCKET: string;
declare const FIREBASE_MESSAGING_SENDER_ID: string;
declare const FIREBASE_APP_ID: string;
declare const FIREBASE_MEASUREMENT_ID: string;

let dbPromise: Promise<any> | null = null;

export enum FirebaseDataTypes {
  NOTIFICATION = 'notification',
}

/**
 * Signs the current user in to Firebase with a custom token minted by the API
 * (uid = `user:<sub>`). The Realtime Database rules only allow a user to read
 * their own `notifications/{uid}` node, so this must complete before any ref
 * is subscribed. Persistence is in-memory on purpose: nothing is left behind
 * on a shared machine after logout, and every page load simply signs in again.
 */
async function signIn(app: any) {
  const [
    { initializeAuth, inMemoryPersistence, signInWithCustomToken },
    { data },
  ] = await Promise.all([import('firebase/auth'), getFirebaseToken()]);
  const auth = initializeAuth(app, { persistence: inMemoryPersistence });
  await signInWithCustomToken(auth, data.token);
}

/**
 * Lazily loads Firebase, signs the user in and returns the database instance.
 * Concurrent callers share one initialisation.
 */
export function getDb() {
  if (!dbPromise) {
    dbPromise = (async () => {
      const [{ initializeApp, getApps }, { getDatabase }] = await Promise.all([
        import('firebase/app'),
        import('firebase/database'),
      ]);

      const firebaseConfig = {
        apiKey: FIREBASE_API_KEY,
        authDomain: FIREBASE_AUTH_DOMAIN,
        databaseURL: FIREBASE_DATABASE_URL,
        projectId: FIREBASE_PROJECT_ID,
        storageBucket: FIREBASE_STORAGE_BUCKET,
        messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
        appId: FIREBASE_APP_ID,
        measurementId: FIREBASE_MEASUREMENT_ID,
      };

      const app = getApps()[0] || initializeApp(firebaseConfig);
      try {
        await signIn(app);
      } catch (err) {
        // Keep the connection going unauthenticated: while the database
        // rules are still open this preserves notifications, and once they
        // are owner-only the read simply fails with permission_denied.
        // eslint-disable-next-line no-console
        console.warn('Firebase sign-in failed', err);
      }
      return getDatabase(app);
    })().catch((err) => {
      dbPromise = null;
      throw err;
    });
  }

  return dbPromise;
}

/**
 * Lazily loads Firebase and returns a database ref for the given query.
 */
export async function getFirebaseRef(query: string) {
  const [{ ref }, database] = await Promise.all([
    import('firebase/database'),
    getDb(),
  ]);
  return ref(database, query);
}
